import classNames from "classnames";
import React, { useEffect } from "react";
import { Overlay } from "../Overlay/Overlay";
import { Svg, SVG_ICONS } from "../Svg/Svg";
import { Typography } from "../Typography/Typography";
import { TTSButton } from "../_controls/TTSButton/TTSButton";

import "./Modal.scss";
import { useTranslation } from "react-i18next";

/**
 * Interface for Modal sub-components.
 *
 * @description Defines the sub-components available on the Modal component.
 */
interface ModalSubComponents {
  Header: React.FC<HeaderProps>;
  Title: React.FC<TitleProps>;
  Body: React.FC<BodyProps>;
  Footer: React.FC<FooterProps>;
}

/**
 * Props for the Modal Header component.
 *
 * @description The Header component is used as the top section of a Modal,
 * typically containing the title and close button.
 *
 * @property {React.ReactNode} [children] - Content to render in the header (usually Modal.Title).
 * @property {() => void} [onHide] - Function called when the close button is clicked.
 * @property {string} [headertestid] - Data-testid attribute for the close button.
 *
 * @example
 * <Modal.Header onHide={handleClose}>
 *   <Modal.Title>Modal Title</Modal.Title>
 * </Modal.Header>
 */
interface HeaderProps {
  children?: React.ReactNode;
  onHide?: () => void;
  headertestid?: string;
}

/**
 * Props for the Modal Title component.
 *
 * @description The Title component displays the modal title with an optional TTS (Text-to-Speech) button.
 *
 * @property {string} [children] - The title text to display.
 * @property {string} [ttsButtonId] - HTML id attribute for the TTS button.
 * @property {boolean} [iconVisibility=true] - Whether to show the TTS button.
 *
 * @example
 * <Modal.Title ttsButtonId="modal-tts">Settings</Modal.Title>
 */
interface TitleProps {
  children?: string;
  ttsButtonId?: string;
  iconVisibility?: boolean;
}

/**
 * Props for the Modal Body component.
 *
 * @description The Body component contains the main content of the modal.
 *
 * @property {string} [id] - HTML id attribute for the body container.
 * @property {React.ReactNode} [children] - Content to render in the modal body.
 * @property {boolean} [isNoFooter=false] - Whether the modal has no footer.
 *   When true, applies additional bottom padding.
 *
 * @example
 * <Modal.Body>
 *   <p>Modal content goes here</p>
 * </Modal.Body>
 */
interface BodyProps {
  id?: string;
  children?: React.ReactNode;
  isNoFooter?: boolean;
}

/**
 * Props for the Modal Footer component.
 *
 * @description The Footer component is used for the bottom section of a Modal,
 * typically containing action buttons.
 *
 * @property {React.ReactNode} [children] - Content to render in the footer (usually buttons).
 *
 * @example
 * <Modal.Footer>
 *   <Button title="Cancel" onClick={handleClose} />
 *   <Button title="Save" type="primary" onClick={handleSave} />
 * </Modal.Footer>
 */
interface FooterProps {
  children?: React.ReactNode;
}

/**
 * Props for the Modal component.
 *
 * @description The Modal component is a dialog box/popup window that is displayed
 * on top of the current page content with an overlay background.
 *
 * @property {boolean} show - Whether the modal is visible. Required.
 * @property {React.ReactNode} [children] - Modal content (Header, Body, Footer components).
 * @property {string} [className] - Additional CSS class names for the modal body.
 * @property {string} [wrapperClassName] - Additional CSS class names for the modal wrapper.
 * @property {boolean} [autoHeight=false] - Whether the modal should auto-size its height.
 * @property {string} [id] - HTML id attribute for the modal wrapper.
 * @property {string} [modalBodyId] - HTML id attribute for the modal body container.
 * @property {number} [tabIndex] - Tab index for keyboard navigation.
 * @property {string} [testId] - Data-testid attribute for testing purposes.
 * @property {boolean} [overlay=true] - Whether to show the overlay background.
 * @property {string} [dir] - Text direction attribute (ltr/rtl).
 *
 * @example
 * // Basic modal
 * <Modal show={isOpen}>
 *   <Modal.Header onHide={() => setIsOpen(false)}>
 *     <Modal.Title>Confirm Action</Modal.Title>
 *   </Modal.Header>
 *   <Modal.Body>
 *     <p>Are you sure you want to proceed?</p>
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button title="Cancel" onClick={() => setIsOpen(false)} />
 *     <Button title="Confirm" type="primary" onClick={handleConfirm} />
 *   </Modal.Footer>
 * </Modal>
 *
 * @example
 * // Modal without footer
 * <Modal show={isOpen} autoHeight>
 *   <Modal.Header onHide={handleClose}>
 *     <Modal.Title>Information</Modal.Title>
 *   </Modal.Header>
 *   <Modal.Body isNoFooter>
 *     <p>Read-only content here</p>
 *   </Modal.Body>
 * </Modal>
 */
interface Props {
  show: boolean;
  children?: React.ReactNode;
  className?: string;
  wrapperClassName?: string;
  autoHeight?: boolean;
  id?: string;
  modalBodyId?: string;
  tabIndex?: number;
  testId?: string;
  overlay?: boolean;
  dir?: string;
}

/**
 * A modal dialog component with composable sub-components.
 *
 * @description Modal renders a dialog box that appears on top of the page content.
 * It uses a compound component pattern with Header, Title, Body, and Footer sub-components
 * for flexible composition. The modal automatically manages body scroll locking when open.
 *
 * Features:
 * - Compound component pattern for flexible composition
 * - Automatic body scroll lock when open
 * - Optional overlay background
 * - Auto-height mode for dynamic content
 * - Text-to-speech support via TTSButton
 * - RTL support via dir prop
 *
 * @param {Props} props - The component props
 * @returns {JSX.Element} A modal dialog with overlay
 *
 * @see Props for all available properties
 * @see Header for modal header component
 * @see Title for modal title component
 * @see Body for modal body component
 * @see Footer for modal footer component
 */
const Modal: React.FC<Props> & ModalSubComponents = (props) => {
  const {
    show,
    children,
    className = "",
    wrapperClassName = "",
    autoHeight = false,
    id,
    modalBodyId,
    tabIndex,
    testId,
    overlay = true,
    dir,
  } = props;
  const wrapClass = classNames(
    "modal-wrapper",
    { open: !!show },
    wrapperClassName
  );
  const bodyClass = classNames("modal", {
    [className]: !!className,
    "auto-height": autoHeight,
  });

  /**
   * Manages body scroll lock when modal is open.
   * Adds 'modal-open' class to prevent background scrolling.
   * Cleans up by removing the class when modal closes or unmounts.
   */
  useEffect(
    function manageBodyScrollLock() {
      const body = document.body;
      if (show) {
        body.classList.add("modal-open");
      } else {
        body.classList.remove("modal-open");
      }

      return () => {
        body.classList.remove("modal-open");
      };
    },
    [show]
  );

  return (
    <div
      className={wrapClass}
      id={id}
      tabIndex={tabIndex}
      data-testid={testId}
      dir={dir}
    >
      <Overlay open={overlay} />
      <div className="modal-content">
        <div className={bodyClass} id={modalBodyId}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * The header section of a Modal component.
 *
 * @description Header renders the top section of a modal with a close button.
 * It typically contains the Modal.Title component and provides the close functionality.
 *
 * @param {HeaderProps} props - The component props
 * @returns {JSX.Element} A header div with close button
 *
 * @see HeaderProps for all available properties
 */
const Header: React.FC<HeaderProps> = (props) => {
  const { t } = useTranslation();
  return (
    <div className="modal-header">
      {props.children}
      <button
        className="modal-header__close__btn"
        data-testid={props.headertestid}
        onClick={props.onHide}
        title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
        {...props}
      >
        <Svg
          icon={SVG_ICONS.DRAWER_CLOSE}
          width={24}
          height={24}
          title={t("PRIVACY_COMMON_CLOSE_ALT_TEXT")}
        />
      </button>
    </div>
  );
};

/**
 * The title component for a Modal header.
 *
 * @description Title renders the modal title text with an optional TTS (Text-to-Speech) button
 * for accessibility. Uses Typography component for consistent text styling.
 *
 * @param {TitleProps} props - The component props
 * @returns {JSX.Element} A title div with optional TTS button
 *
 * @see TitleProps for all available properties
 */
const Title: React.FC<TitleProps> = (props) => {
  const { children, iconVisibility = true, ttsButtonId } = props;
  return (
    <div className="modal-title">
      <Typography variant="title2">
        {children}
        {iconVisibility ? (
          <TTSButton
            value={props.children}
            className="tts-button-modal"
            id={ttsButtonId}
          />
        ) : null}
      </Typography>
    </div>
  );
};

/**
 * The body section of a Modal component.
 *
 * @description Body renders the main content area of the modal. It supports
 * a no-footer mode that adds additional bottom padding when no footer is present.
 *
 * @param {BodyProps} props - The component props
 * @returns {JSX.Element} A body div for modal content
 *
 * @see BodyProps for all available properties
 */
const Body: React.FC<BodyProps> = (props) => {
  const { isNoFooter = false, id } = props;
  return (
    <div
      className={classNames("modal-body", { "no-footer": isNoFooter })}
      id={id}
    >
      {props.children}
    </div>
  );
};

/**
 * The footer section of a Modal component.
 *
 * @description Footer renders the bottom section of the modal, typically used
 * for action buttons like Save, Cancel, etc.
 *
 * @param {FooterProps} props - The component props
 * @returns {JSX.Element} A footer div for modal actions
 *
 * @see FooterProps for all available properties
 */
const Footer: React.FC<FooterProps> = (props) => (
  <div className="modal-footer">{props.children}</div>
);

Modal.Header = Header;
Modal.Title = Title;
Modal.Body = Body;
Modal.Footer = Footer;

export default Modal;
